import { useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import { ALL_OPTION_KEY, RAW_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos';

import { useGetEmployerIdParam } from '../../hooks';
import { useBaseReportTableQuery } from '../../queries';
import { CardStatusReportTableParams } from '../CardStatusReport.types';

export const QUERY_KEY = 'CARD_STATUS_REPORT_QUERY';

const MIN_SEARCH_LEN = 3;
const useCardStatusReportQuery = (params: Partial<CardStatusReportTableParams>) => {
  const currentSearch = useMemo(() => {
    if (!!params?.searchString && params?.searchString?.length >= MIN_SEARCH_LEN) {
      return params.searchString;
    }
    return null;
  }, [params.searchString]);

  const formattedStartDate = useMemo(() => {
    if (!params.mailedStartDate) {
      return null;
    }
    const parsedDate = dayjs(params.mailedStartDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.mailedStartDate]);

  const formattedEndDate = useMemo(() => {
    if (!params.mailedEndDate) {
      return null;
    }
    const parsedDate = dayjs(params.mailedEndDate);
    if (!parsedDate.isValid()) {
      return null;
    }
    return parsedDate.format(RAW_DATE_FORMAT);
  }, [params.mailedEndDate]);

  const employerIdsString = useMemo(() => params?.employerIds?.join(','), [params?.employerIds]);
  const getEmployerIdParam = useGetEmployerIdParam();
  const base = useBaseReportTableQuery(
    API_URL.REPORTS_CARD_STATUS_REPORT_BREAKDOWN,
    [
      QUERY_KEY,
      currentSearch,
      params.employerId,
      params.employmentStatuses?.join(','),
      params.cardStatuses?.join(','),
      params.mailedStartDate,
      params.mailedEndDate,
      employerIdsString,
    ],
    {
      ...getEmployerIdParam(params.employerId, params.employerIds),
      show_ssn: true,
      mailed_start_date: formattedStartDate,
      mailed_end_date: formattedEndDate,
      isMultipleEmployers: params.isMultipleEmployer,
      card_statuses:
        params?.cardStatuses && !params?.cardStatuses.includes(ALL_OPTION_KEY)
          ? params?.cardStatuses.join(',')
          : undefined,

      employment_statuses:
        params?.employmentStatuses && !params?.employmentStatuses.includes(ALL_OPTION_KEY)
          ? params?.employmentStatuses.join(',')
          : undefined,

      isSSNShowed: params.isSSNShowed,
      search_value: currentSearch,
    },
    {
      isMultipleEmployers: params.isMultipleEmployer,
      isPreviewMode: params.isPreviewMode,
    }
  );

  useEffect(() => {
    base.onChangePlanOptions([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.employerId, employerIdsString]);

  useEffect(() => {
    base.setPage(0);
    base.setRawContentList([]);
    base.setRawHeaders([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    params.employerId,
    params?.searchString,
    employerIdsString,
    params?.mailedStartDate,
    params?.mailedStartDate,
    params?.cardStatuses,
    params?.employmentStatuses,
  ]);
  return base;
};

export default useCardStatusReportQuery;
